import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import type { SampleDocType } from '@ast-data/shared';
import { useProjectDocs } from '../../db/useScopedDocs';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import FieldCardList, { type FieldCardRow } from './FieldCardList';
import type { FieldSortOption } from './fieldListSort';

const SORT = {
    listId: 'samples',
    options: [
        { key: 'date', labelId: 'field.sort.date' },
        { key: 'sampleType', labelId: 'sample.field.sampleType' }
    ] satisfies FieldSortOption[]
};
import { sampleLabel } from '../findings/sampleLabel';
import { formatDate } from '../../utils/formatDate';

/**
 * Touch-first list of a project's Proben, with fast add. Selecting one (or Add)
 * opens the office fullscreen dialog over this list — see
 * [[field-reuses-office-dialogs]].
 */
export default function FieldSamplesPage() {
    const intl = useIntl();
    const navigate = useNavigate();
    const { projectId = '' } = useParams<{ projectId: string }>();

    const { results: samples } = useProjectDocs<SampleDocType>(
        'samples',
        projectId
    );

    const canCreate = useAbility().can(
        'create',
        scopedSubject('Sample', { projectId })
    );

    const [search, setSearch] = useState('');

    const rows = useMemo<FieldCardRow[]>(
        () =>
            samples.map((sample) => ({
                id: sample.id,
                title: sampleLabel(intl, sample),
                subtitle:
                    [sample.sampleType, sample.sampler]
                        .filter(Boolean)
                        .join(' · ') || undefined,
                sortKey: sample.recordedAt,
                sortValues: {
                    date: sample.recordedAt,
                    sampleType: sample.sampleType
                },
                searchText: [
                    formatDate(sample.recordedAt),
                    sample.sampleType,
                    sample.sampler
                ]
                    .filter(Boolean)
                    .join(' ')
            })),
        [samples, intl]
    );

    const handleSelect = useCallback(
        (id: string) => navigate(`/field/${projectId}/samples/${id}/edit`),
        [navigate, projectId]
    );

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <FieldCardList
                rows={rows}
                onSelect={handleSelect}
                emptyMessageId="field.samples.empty"
                noMatchesMessageId="field.samples.noMatches"
                sort={SORT}
                search={search}
                onSearchChange={setSearch}
                toolbar={
                    canCreate ? (
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                navigate(`/field/${projectId}/samples/new`)
                            }
                        >
                            <FormattedMessage id="sample.add" />
                        </Button>
                    ) : undefined
                }
            />
            {/* The Probe dialog routes render here, over the list. */}
            <Outlet />
        </Box>
    );
}
